import { useState, useEffect } from 'react'
import { Plus, TrendingUp, TrendingDown, Trash2 } from 'lucide-react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { PageHeader } from '@/components/layout'
import { StatCard } from '@/components/shared'
import { useAppDispatch, useAppSelector } from '@/store/hooks'
import { addInvestment, removeInvestment } from '@/store/slices/portfolioSlice'
import { visitPage } from '@/store/slices/gamificationSlice'
import { formatCurrency, formatPercentage } from '@/lib/utils'
import type { AssetType } from '@/types'

export default function Portfolio() {
  const dispatch = useAppDispatch()
  const { investments, totalValue, allocation } = useAppSelector(state => state.portfolio)
  const [dialogOpen, setDialogOpen] = useState(false)

  // Track page visit for explorer achievement
  useEffect(() => {
    dispatch(visitPage('portfolio'))
  }, [dispatch])
  const [newInvestment, setNewInvestment] = useState({
    symbol: '',
    name: '',
    shares: 0,
    purchasePrice: 0,
    currentPrice: 0,
    type: 'stocks' as AssetType,
  })

  const handleAddInvestment = () => {
    if (!newInvestment.symbol || !newInvestment.name) return
    
    dispatch(addInvestment({
      ...newInvestment,
      currentValue: newInvestment.shares * newInvestment.currentPrice,
    }))
    
    setNewInvestment({
      symbol: '',
      name: '',
      shares: 0,
      purchasePrice: 0,
      currentPrice: 0,
      type: 'stocks',
    })
    setDialogOpen(false)
  }

  const handleRemoveInvestment = (index: number) => {
    dispatch(removeInvestment(index))
  }

  const calculateGainLoss = (purchasePrice: number, currentPrice: number, shares: number) => {
    const costBasis = purchasePrice * shares
    const currentValue = currentPrice * shares
    const gainLoss = currentValue - costBasis
    const percentage = costBasis > 0 ? (gainLoss / costBasis) * 100 : 0
    return { gainLoss, percentage }
  }

  return (
    <div className="space-y-6">
      <PageHeader 
        title="Portfolio" 
        description="Track and manage your investment portfolio."
      >
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button>
              <Plus className="mr-2 h-4 w-4" />
              Add Investment
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add Investment</DialogTitle>
              <DialogDescription>
                Add a new investment to your portfolio.
              </DialogDescription>
            </DialogHeader>
            <div className="grid gap-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="symbol">Symbol</Label>
                  <Input
                    id="symbol"
                    placeholder="AAPL"
                    value={newInvestment.symbol}
                    onChange={(e) => setNewInvestment({ ...newInvestment, symbol: e.target.value.toUpperCase() })}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="type">Type</Label>
                  <Select
                    value={newInvestment.type}
                    onValueChange={(value: AssetType) => setNewInvestment({ ...newInvestment, type: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="stocks">Stocks</SelectItem>
                      <SelectItem value="bonds">Bonds</SelectItem>
                      <SelectItem value="crypto">Crypto</SelectItem>
                      <SelectItem value="cash">Cash</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="name">Name</Label>
                <Input
                  id="name"
                  placeholder="Apple Inc."
                  value={newInvestment.name}
                  onChange={(e) => setNewInvestment({ ...newInvestment, name: e.target.value })}
                />
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="shares">Shares</Label>
                  <Input
                    id="shares"
                    type="number"
                    value={newInvestment.shares || ''}
                    onChange={(e) => setNewInvestment({ ...newInvestment, shares: parseFloat(e.target.value) || 0 })}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="purchasePrice">Cost Basis</Label>
                  <Input
                    id="purchasePrice"
                    type="number"
                    value={newInvestment.purchasePrice || ''}
                    onChange={(e) => setNewInvestment({ ...newInvestment, purchasePrice: parseFloat(e.target.value) || 0 })}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="currentPrice">Current Price</Label>
                  <Input
                    id="currentPrice"
                    type="number"
                    value={newInvestment.currentPrice || ''}
                    onChange={(e) => setNewInvestment({ ...newInvestment, currentPrice: parseFloat(e.target.value) || 0 })}
                  />
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
              <Button onClick={handleAddInvestment}>Add Investment</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </PageHeader>

      {/* Summary Stats */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <StatCard
          title="Total Value"
          value={formatCurrency(totalValue)}
          icon={TrendingUp}
        />
        <StatCard
          title="Stocks"
          value={formatPercentage(allocation.stocks)}
          icon={TrendingUp}
        />
        <StatCard
          title="Bonds"
          value={formatPercentage(allocation.bonds)}
          icon={TrendingDown}
        />
        <StatCard
          title="Holdings"
          value={investments.length.toString()}
          icon={TrendingUp}
        />
      </div>

      {/* Holdings Table */}
      <Card>
        <CardHeader>
          <CardTitle>Holdings</CardTitle>
          <CardDescription>Your current investment holdings</CardDescription>
        </CardHeader>
        <CardContent>
          {investments.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <p>No investments yet. Add your first investment to get started.</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Symbol</TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead className="text-right">Shares</TableHead>
                  <TableHead className="text-right">Price</TableHead>
                  <TableHead className="text-right">Value</TableHead>
                  <TableHead className="text-right">Gain/Loss</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {investments.map((inv, index) => {
                  const { gainLoss, percentage } = calculateGainLoss(inv.purchasePrice, inv.currentPrice, inv.shares)
                  const isPositive = gainLoss >= 0
                  
                  return (
                    <TableRow key={inv.id || index}>
                      <TableCell className="font-medium">{inv.symbol}</TableCell>
                      <TableCell>{inv.name}</TableCell>
                      <TableCell>
                        <Badge variant="secondary" className="capitalize">
                          {inv.type}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">{inv.shares}</TableCell>
                      <TableCell className="text-right">{formatCurrency(inv.currentPrice)}</TableCell>
                      <TableCell className="text-right font-medium">{formatCurrency(inv.currentValue)}</TableCell>
                      <TableCell className={`text-right ${isPositive ? 'text-success' : 'text-destructive'}`}>
                        <div className="flex items-center justify-end gap-1">
                          {isPositive ? <TrendingUp className="h-4 w-4" /> : <TrendingDown className="h-4 w-4" />}
                          {formatCurrency(gainLoss)} ({formatPercentage(percentage)})
                        </div>
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleRemoveInvestment(index)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
